import * as Sentry from '@sentry/react'
import type { ErrorEvent } from '@sentry/react'
import type { RootOptions } from 'react-dom/client'

// This entrypoint can also be evaluated during SSR. Only browsers send events.
type SentryRootOptions = Pick<RootOptions, 'onUncaughtError' | 'onCaughtError' | 'onRecoverableError'>

export function initializeSentry(env: Pick<ImportMetaEnv, 'PROD'> & Partial<Record<'VITE_SENTRY_DSN' | 'VITE_SENTRY_RELEASE', string>> = import.meta.env): SentryRootOptions {
  if (typeof window === 'undefined' || !env.PROD || !env.VITE_SENTRY_DSN) return {}

  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment: 'production',
    release: env.VITE_SENTRY_RELEASE || undefined,
    // Sentry 11 collects these categories by default. Keep browser reports private.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
      graphQL: { document: false, variables: false },
      stackFrameVariables: false,
    },
    // Avoid collecting console output, DOM interactions and navigation URLs,
    // which may contain chat content or authentication parameters.
    integrations: (defaults) => defaults.filter((integration) => integration.name !== 'Breadcrumbs'),
    beforeSend: sanitizeSentryEvent,
    initialScope: { tags: { service: 'web-browser' } },
  })

  return {
    onUncaughtError: Sentry.reactErrorHandler(),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  }
}

export function sanitizeSentryEvent(event: ErrorEvent): ErrorEvent {
  // Keep stack traces, but omit request data and user context from browser reports.
  delete event.request
  delete event.user
  delete event.breadcrumbs
  return event
}
